import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import EventsApi from '../../../api/eventsApi/eventsApi';
import { useSelector } from 'react-redux';
import ApiBase from '../../../api/apiBase';
import { useNavigate } from 'react-router-dom';

interface ReportPinModalProps {
    open: boolean;
    onClose: () => void;
    coordinates?: {
        latitude: number;
        longitude: number;
    };
    radius?: number;
    wikipediaData: {
        title: string | null;
        summary: string | null;
    };
    onSubmit: (data: any) => void;
}

interface ReportPinData {
    name: string;
    radius: number;
    sector: string;
    id?: string;
}

interface QuestionCategoryResponse {
    id: string;
    name: string;
    userId: string;
    questionTypeId: string;
    pinId: string;
}

interface QuestionResponse {
    id: string;
    text: string;
    userId: string;
    questionCategoryId: string;
}

const ReportPinModal: React.FC<ReportPinModalProps> = ({
    open,
    onClose,
    onSubmit,
    coordinates,
    radius,
    wikipediaData
}) => {
    const navigate = useNavigate();
    const userId = useSelector((state: any) => state.auth.id);
    const [reportPinData, setReportPinData] = useState<ReportPinData>({
        name: '',
        radius: radius ? Math.round(radius) : 5000,
        sector: ''
    });

    const PRESET_RADII = [5000, 10000, 15000];
    const initialRadius = radius ? Math.round(radius) : 5000;
    const [radiusOption, setRadiusOption] = useState<string>(
        PRESET_RADII.includes(initialRadius) ? String(initialRadius) : 'custom'
    );
    const [customRadius, setCustomRadius] = useState<string>(
        PRESET_RADII.includes(initialRadius) ? '' : String(initialRadius)
    );
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (radius) {
            const r = Math.round(radius);
            setReportPinData(prev => ({ ...prev, radius: r }));
            if (PRESET_RADII.includes(r)) {
                setRadiusOption(String(r));
                setCustomRadius('');
            } else {
                setRadiusOption('custom');
                setCustomRadius(String(r));
            }
        }
    }, [radius]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setReportPinData(prev => ({
            ...prev,
            [name]: name === 'radius' ? Number(value) : value
        }));
    };

    const handleRadiusOptionChange = (value: string) => {
        setRadiusOption(value);
        if (value === 'custom') {
            const current = reportPinData.radius && reportPinData.radius > 0 ? reportPinData.radius : 0;
            setCustomRadius(current ? String(current) : '');
            return;
        }
        const r = Number(value);
        setCustomRadius('');
        setReportPinData(prev => ({ ...prev, radius: r }));
    };

    const checkDuplicateCoordinates = async (latitude: number, longitude: number): Promise<boolean> => {
        try {
            console.log('Checking coordinates:', { latitude, longitude });
            const existingPins = await EventsApi.getPins();
            console.log('Existing pins response:', existingPins);

            if (existingPins && Array.isArray(existingPins)) {
                console.log('Number of existing pins:', existingPins.length);

                const duplicate = existingPins.some(pin => {
                    const isDuplicate =
                        Math.abs(pin.latitude - latitude) < 0.0001 &&
                        Math.abs(pin.longitude - longitude) < 0.0001;
                    if (isDuplicate) {
                        console.log('Found duplicate pin:', pin);
                    }
                    return isDuplicate;
                });

                console.log('Duplicate found:', duplicate);
                return duplicate;
            }
            console.log('No valid pins data found');
            return false;
        } catch (error) {
            console.error('Error checking duplicate coordinates:', error);
            return false;
        }
    };

    const handleContinue = async () => {
        if (!coordinates) {
            setError('Koordinat bilgisi bulunamadı');
            return;
        }

        setIsLoading(true);
        try {
            console.log('Starting coordinate check with:', coordinates);
            const hasDuplicate = await checkDuplicateCoordinates(coordinates.latitude, coordinates.longitude);
            console.log('Duplicate check result:', hasDuplicate);

            if (hasDuplicate) {
                setError('Bu koordinatlarda kayıtlı bir tesis bulunmaktadır. Lütfen farklı bir konum seçiniz.');
                setIsLoading(false);
                return;
            }

            // Get user data to access organizationId
            const userData = await EventsApi.getUser();

            // Wikipedia verilerini JSON olarak hazırla
            const questionsJsonData = {
                wikipediaTitle: wikipediaData?.title || null,
                wikipediaSummary: wikipediaData?.summary || null
            };

            // JSON'ı Blob'a çevir ve File oluştur
            const jsonBlob = new Blob([JSON.stringify(questionsJsonData)], { type: 'application/json' });
            const jsonFile = new File([jsonBlob], 'questions.json', { type: 'application/json' });

            const formData = new FormData();
            formData.append('Name', reportPinData.name);
            formData.append('Latitude', coordinates.latitude.toString());
            formData.append('Longitude', coordinates.longitude.toString());
            formData.append('RadiusInMeters', reportPinData.radius.toString());
            formData.append('Sector', reportPinData.sector);
            formData.append('userId', userId);
            formData.append('organizationId', userData.organizationId);
            formData.append('Files', jsonFile);

            const response = await EventsApi.createNewPin(formData);

            if (response?.id) {
                // Yeni pin oluşturulduktan sonra soru kategorisi oluştur
                const categoryResponse = await ApiBase.postQuestionPromise<QuestionCategoryResponse>('/api/QuestionCategories', {
                    name: "SONUÇ VE DEĞERLENDİRME",
                    userId: userId,
                    questionTypeId: "d5d3dd45-0a2e-457d-933b-c66a3853469f",
                    pinId: response.id
                });

                if (categoryResponse?.id) {
                    // Soru kategorisi oluşturulduktan sonra soru oluştur
                    await EventsApi.saveQuestion({
                        text: "Sonuç ve Değerlendirme",
                        userId: userId,
                        questionCategoryId: categoryResponse.id,
                        pinId: response.id
                    });
                }

                onSubmit({
                    ...reportPinData,
                    id: response.id
                });
                onClose();
                // Reports sayfasına yönlendir
                navigate(`/reports/${response.id}`);
                setError(null);
            } else {
                console.error('Response içinde id bulunamadı:', response);
                throw new Error('Pin ID alınamadı');
            }
        } catch (err) {
            setError('Pin kaydedilirken bir hata oluştu');
            console.error('Pin kaydetme hatası:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: { width: '400px', p: 1 }
            }}
        >
            <DialogTitle>Yeni Lokasyon Ekle</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
                    <TextField
                        label="Lokasyon Adı"
                        name="name"
                        value={reportPinData.name}
                        onChange={handleChange}
                        fullWidth
                        required
                    />
                    <FormControl fullWidth required>
                        <InputLabel id="radius-select-label">Yarıçap (metre)</InputLabel>
                        <Select
                            labelId="radius-select-label"
                            value={radiusOption}
                            label="Yarıçap (metre)"
                            onChange={(e) => handleRadiusOptionChange(e.target.value as string)}
                        >
                            <MenuItem value="5000">5000</MenuItem>
                            <MenuItem value="10000">10000</MenuItem>
                            <MenuItem value="15000">15000</MenuItem>
                            <MenuItem value="custom">Diğer</MenuItem>
                        </Select>
                    </FormControl>

                    {radiusOption === 'custom' && (
                        <TextField
                            label="Diğer yarıçap (metre)"
                            name="radius"
                            type="number"
                            value={customRadius}
                            onChange={(e) => {
                                const v = e.target.value;
                                setCustomRadius(v);
                                setReportPinData(prev => ({ ...prev, radius: Number(v) }));
                            }}
                            fullWidth
                            required
                            inputProps={{
                                min: 0,
                                step: 1
                            }}
                        />
                    )}
                    <TextField
                        label="Sektör"
                        name="sector"
                        value={reportPinData.sector}
                        onChange={handleChange}
                        fullWidth
                        required
                    />
                    {error && (
                        <Box sx={{ color: 'error.main', mt: 1 }}>
                            {error}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        setReportPinData({
                            name: '',
                            radius: 0,
                            sector: ''
                        });
                        onClose();
                    }}
                    fullWidth
                    sx={{
                        backgroundColor: '#fff',
                        border: 1,
                        borderColor: '#094151',
                        color: "#081038",
                        borderWidth: '2px',
                        '&:hover': {
                            borderWidth: '2px',
                            backgroundColor: '#E9F8FC',
                            color: "#081038"
                        }
                    }}
                >
                    İptal
                </Button>
                <Button
                    onClick={handleContinue}
                    fullWidth
                    disabled={!reportPinData.name || !reportPinData.radius || !reportPinData.sector || isLoading}
                    sx={{
                        backgroundColor: '#094151',
                        color: "#fff",
                        '&:hover': {
                            backgroundColor: '#022631',
                            color: "#fff", fontWeight: 600
                        },
                        '&:disabled': {
                            backgroundColor: 'rgba(9, 65, 81, 0.75)', color: "#fff"
                        },
                    }}
                >
                    {isLoading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>Kaydediliyor...</span>
                        </Box>
                    ) : (
                        'Kaydet'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ReportPinModal;